import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducer';
import {clearSettings, setEffectivePermissions, setSelectedOrganization} from "../../store/settingsSlice";
import {SettingsDAL} from "../../dal/SettingsDAL";
import api, {
    useClearExtensionsMutation,
    useClearWorkspaceCacheMutation,
    useCreateSearchSessionMutation,
} from "../../store/Api";
import {useClearSearchMutation} from "../../store/endpoints/SavedSearchEndpoints";
import {
    useLazyGetOrganizationsQuery,
    useSetSelectedOrganizationMutation
} 
    from "../../store/endpoints/OrganizationsEndpoints"
import {useMsal} from "@azure/msal-react";
import {updateLoggingOut, updateLoggingOutError} from "../../store/settingsSlice";

const PermissionsHandler: React.FC = () => {
    const auth = useSelector((state: RootState) => state.auth);
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const dispatch = useDispatch();
    const [searchSession] = useCreateSearchSessionMutation();
    const [getOrganizations] = useLazyGetOrganizationsQuery();
    const [clearWorkspaces] = useClearWorkspaceCacheMutation();
    const [clearSearch] = useClearSearchMutation();
    const [setOrganization] = useSetSelectedOrganizationMutation();
    const [clearExtensions] = useClearExtensionsMutation();
    const {instance} = useMsal();

    const clearCache = async (clearSelectedOrg = false) => {
        try {
            await clearSearch();
            dispatch(clearSettings());
            await clearWorkspaces();
            await clearExtensions();
            if (clearSelectedOrg)
                await setOrganization(undefined);
            dispatch(api.util.resetApiState());
        } catch (error) {
            console.error("Error clearing cache:", error);
        }
    }

    const logOut = (error?: string) => {
        if (error) {
            dispatch(updateLoggingOutError(error));
        }

        dispatch(updateLoggingOut(true));
        clearCache(true).then(() => {
            instance.logoutRedirect()
        });
    }

    React.useEffect(() => {
        const fetchPermissions = async () => {
            if (auth.isAuthed) {
                const dal = new SettingsDAL(auth, organization);
                const results = await dal.getPermissions();
                dispatch(setEffectivePermissions(results?.permissions));
                if(!auth.searchSession){
                    searchSession(undefined)
                }
            }
        }
        fetchPermissions()
            .catch(console.error)
    }, [auth]);

    // A support session is bound to one organization by the API; there is no list to choose from, and the support
    // username belongs to no organization, so the lookup below would fail and sign the person out.
    React.useEffect(() => {
        if (auth.supportSession && organization !== auth.supportSession.organization) {
            dispatch(setSelectedOrganization(auth.supportSession.organization));
        }
    }, [auth.supportSession, organization]);

    React.useEffect(() => {
        if (auth.isAuthed && auth.userName && !auth.supportSession) {
            getOrganizations({username: auth.userName})
            .then((response) => {
                if(response.error) {
                    console.error('Error fetching organizations', response.error);
                    logOut("No valid organizations found. If you believe this is in error, contact support@adaptivecatalog.com");
                }
                if (response.data) {
                    if (organization) {
                        const org = response.data.find(org => org.id === organization);
                        if (org) {
                            dispatch(setSelectedOrganization(org.id));
                        }
                    } else {
                        dispatch(setSelectedOrganization(response.data[0].id));
                    }
                }
            })
        }
    }, [auth, organization])


    return <></>;
}

export default PermissionsHandler;